/* eslint-disable @typescript-eslint/no-explicit-any */
// トークンの有効期限切れ、キャッシュ強制クリアにより未認証状態になった場合はTOP画面へ戻る。
// サインアウト: 読み出し元にTOP画面への遷移を実装
// 認証チェックの結果未認証発覚: 読み出し元にTOP画面への遷移を実装
// パスワード更新時にトークン取得失敗: 読み出し元にTOP画面への遷移を実装
// S3アクセスの際にトークン取得または、トークンリフレッシュ失敗: トークン取得またはリフレッシュ失敗時にTOP画面への遷移を実装(Mixin)
// API-GWアクセスの際にトークン取得または、トークンリフレッシュ失敗
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { useRouter } from 'vue-router'
import {
  type ErrorMessage,
  confirm,
  alert,
  setLoadingView,
  promptDialog,
  webApiCall,
  tempDataSetItem,
  tempDataRemoveItem,
  tempDataGetItem,
  getSessionInfo,
  RemoveSessionInfo
} from './utils/miiaFwUtils'
import MiiaFwConfig from '@/configs/config'
import { config, CognitoIdentityCredentials } from 'aws-sdk' // 【後で削除】クレデンシャルの更新に必要(S3の更新に必要): トークン取得またはリフレッシュ失敗時にTOP画面への遷移を実装(Mixin)
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import apigClientFactory, {
  type AdditionalParams,
  type KeyValues
} from '../../libs/awsApiGatewayClient/awsApiGatewayClient'

if (MiiaFwConfig.config.authorization.cognitoIdentityEndpoint) {
  config.cognitoidentity = { endpoint: MiiaFwConfig.config.authorization.cognitoIdentityEndpoint }
}

/**
 * @module components/miiafw/mixin/MiiaFwCognito
 * @description 認証や、属性情報を取得する機能を集合させたライブラリ。
 */

/** 本ライブラリのグローバル変数 */
const authApiSubdir: string =
  Object.prototype.hasOwnProperty.call(MiiaFwConfig.config.authorization, 'authSubDir') &&
  typeof MiiaFwConfig.config.authorization.authSubDir === 'string'
    ? MiiaFwConfig.config.authorization.authSubDir
    : 'miiafw-auth' // 標準認証用

// Interfaces
/** トークン情報格納用DTO */
export interface TokenInfo {
  /** アクセストークン */
  access_token?: string
  /** リフレッシュトークン */
  refresh_token?: string
  /** IDトークン */
  id_token?: string
  /** デバイスキー */
  device_key?: string
  /** Authトークン */
  auth_token?: string
  /** トークンシークレット */
  token_secret?: string
  /** IDプロバイダ */
  provider: string
  /** セッションID */
  session_id?: string
}

/** サインインステータスを格納するオブジェクト */
export interface SignInInfo {
  /** 有効期限切れかどうか */
  isExpired: boolean
  /** 認証がアップデートされたかどうか */
  isUpdated: boolean
  /** ユーザーID */
  userId: string
  /** セッション情報 */
  tokens: TokenInfo
}

/** サインアップメソッドの引数(params) */
interface SignupParams {
  /** ユーザID */
  user_id: string
  /** メールアドレス */
  email?: string
  /** 携帯電話番号 */
  phone_number?: string
  /** パスワード */
  password: string
  /** システムID */
  system_id: string
}

/** リフレッシュトークンメソッドの引数(params) */
interface RefreshTokenParams {
  /** リフレッシュトークン */
  refresh_token?: string
  /** デバイスキー */
  device_key?: string
  /** アクセストークン */
  access_token?: string
}

/** パスワード更新メソッドの引数(params) */
interface UpdatePasswordParams {
  /** 現在のパスワード */
  current_password: string
  /** 更新後のパスワード */
  new_password: string
  /** アクセストークン */
  access_token?: string
}

/** サインアウトメソッドの引数(params) */
interface SignoutParams {
  /** アクセストークン */
  access_token?: string
}

/** トークン有効性確認メソッドの引数(params) */
interface TokenCheckParams {
  /** アクセストークン */
  access_token?: string
}

/** エラーコード */
export const ERROR_CODE: { [key: string]: number } = Object.freeze({
  FORBIDDEN: 403,
  SERVICE_UNAVAILABLE: 503,
  CLIENT_REQUEST_TIMEOUT: 600
})

const router = useRouter()

/**
 * Vuexにストアされている'miiaFwStore/updateAuthInfo'を更新する。
 * ちなみにmiiaFwStore/updateAuthInfoは現在使用されていない。
 * @param isAutAuthInfo 'miiaFwStore/updateAuthInfo'の値を変更する。
 */
export const updateAuthInfo = (isAuthorized: boolean): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateAuthInfo(isAuthorized)
}

/**
 * Vuexにストアされている'miiaFwStore/updateMenu'を更新する。
 * 表示するメニュー項目の設定を行うときに実行する。
 * @param menu Vuerouterのroutesの要素を並べた配列。
 */
export const updateMenu = (menu: any[]): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateMenu(menu)
}

/**
 * Vuexにストアされている'miiaFwStore/updateAttributes'を更新する。
 * 属性情報を更新するときに使用する。
 * @param attributes 認可情報
 */
export const updateAttributes = (attributes: { [key: string]: string }): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateAttributes(attributes)
}

/**
 * Vuexにストアされている'miiaFwStore/updateSensitiveInfo'を更新する。
 * センシティブ情報を設定するときに使用する
 * @param obj センシティブ情報
 */
export const updateSensitiveInfo = (obj: { [key: string]: string }): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateSensitiveInfo(obj)
}

/**
 * Vuexにストアされている'miiaFwStore/updateIsAuthorized'を更新する。
 * 認証が新しく実行されて、成功した際にtrueにする。
 * @param isAuthorized 認証が更新に成功したかどうか。
 */
export const updateIsAuthorized = (isAuthorized: boolean): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateIsAuthorized(isAuthorized)
}

/**
 * Vuexにストアされている属性情報('miiaFwStore/updateAttributes')、
 * センシティブ情報('miiaFwStore/updateSensitiveInfo')、
 * 認証情報('miiaFwStore/updateIsAuthorized')をクリーンアップする。
 */
export const cleanupAuthInfoInStore = (): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateAttributes(undefined) // cognitoAuth.attributesの初期値はundefined
  miiaFwStore.updateSensitiveInfo(undefined) // cognitoAuth.sensitiveInfoの初期値はundefined
  miiaFwStore.updateIsAuthorized(false)
}

/** リフレッシュが必要な状態かどうかをストアするオブジェクト */
export interface RefreshState {
  /** リフレッシュ処理が必表かどうか */
  onRefresh: boolean
  error?: string | null
}

/**
 * Vuexにストアされている'miiaFwStore/updateRefreshState'を書き換える。
 * トークンのリフレッシュ処理が必要な時にtrueに切り変わる。
 * @param onRefresh リフレッシュ処理が必要かどうか
 * @param errorMessage リフレッシュ処理の際にエラーが発生した時に格納するエラーメッセージ。
 */
export const updateRefreshState = (
  onRefresh: boolean | RefreshState,
  errorMessage?: string | undefined | null
): void => {
  if (!errorMessage === undefined) {
    const miiaFwStore = useMiiaFwStore()
    miiaFwStore.updateRefreshState(
      typeof onRefresh === 'boolean' ? { onRefresh: onRefresh } : onRefresh
    )
  } else {
    const miiaFwStore = useMiiaFwStore()
    miiaFwStore.updateRefreshState(
      typeof onRefresh === 'boolean'
        ? {
            onRefresh: onRefresh,
            error: errorMessage
          }
        : onRefresh
    )
  }
}

/**
 * Cognitoのサインアップ処理を行う。
 * @param userName ユーザーID
 * @param email メールアドレス
 * @param password パスワード
 * @returns If resolved then 'サインアップに成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const signup = async (
  userName: string,
  email: string,
  phoneNumber: string,
  password: string
): Promise<string | ErrorMessage> => {
  const signupParams: SignupParams = {
    user_id: userName,
    password: password,
    system_id: MiiaFwConfig.config.sysId
  }
  if (email.length > 0) {
    signupParams.email = email
  }
  if (phoneNumber.length > 0) {
    signupParams.phone_number = phoneNumber
  }
  const args = { function: 'sign_up', params: signupParams }
  const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/**
 * Cognitoのサインアップしたユーザーを認証コードを用いてアクティベートする。
 * @param userName ユーザー
 * @param verificationCode 認証コード
 * @returns If resolved then 'アクティベートに成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const activate = async (
  userName: string,
  verificationCode: string
): Promise<string | ErrorMessage> => {
  const args = {
    function: 'activate',
    params: {
      user_id: userName,
      verification_code: verificationCode
    }
  }
  const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/** 認証後に払い出される情報を格納したオブジェクト */
export interface AuthInfo {
  /** 有効期限切れかどうか */
  isExpired: boolean
  /** 認証がアップデートされたかどうか */
  isUpdated: boolean
  /**  ユーザーID */
  userId: string
  /** セッション情報` */
  tokens: TokenInfo
  /** 認証の種類 */
  challengeName: string
  /** セッション */
  session: string
  /** MFA認証が必要かどうか */
  needMfa: boolean
}

interface BodyOfSigninApi {
  function: string
  params: {
    user_id: string | null
    password?: string | null
    system_id: string
    challenge_name?: string | null
    session?: string | null
    mfa_code?: string | null
    expiration_date_n?: number
  }
}

/**
 * Cognitoのサインイン処理を行う。
 * サインインに成功した場合はセッションストレージの認証情報を更新してから、認証情報を返す。
 * @param userName ユーザーID
 * @param password パスワード
 * @returns If resolved then
 * {'isExpired': (boolean)有効期限切れかどうか,
 * 'isUpdated': (boolean->true) 認証がアップデートされたかどうか,
 * 'userId': (string)ユーザーID,
 * 'tokens': {'provider': (string->'cognito')認証を提供するプロバイダ,
 * 'access_token': (string)アクセストークン,
 * 'refresh_token': (string)リフレッシュトークン,
 * 'id_token': (string)IDトークン,
 * 'session_id': (string)セッションID,
 * 'device_key': (string)デバイスキー }}.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const signin = async (
  userName: string | null,
  password: string | null
): Promise<AuthInfo | ErrorMessage> => {
  const args: BodyOfSigninApi = {
    function: 'sign_in',
    params: {
      user_id: userName,
      password: password,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  if (MiiaFwConfig.config.authorization.expirationTime < Number.MAX_VALUE) {
    args.params.expiration_date_n = MiiaFwConfig.config.authorization.expirationTime
  }
  const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          if (Object.prototype.hasOwnProperty.call(data.result, 'tokens')) {
            setTokens(data.result.tokens)
            updateIsAuthorized(true)
            setUserIdToStorage(userName as string)
            setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
            updateCredentials(data.result.tokens) // 【後で削除】S3でAWS SDKを使用するために必要
            resolve(data.result)
          } else if (
            Object.prototype.hasOwnProperty.call(data.result, 'challengeName') &&
            Object.prototype.hasOwnProperty.call(data.result, 'session')
          ) {
            resolve(data.result)
          } else {
            reject({ message: 'sign in APIが想定外の実行結果を返しました。' })
          }
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        // procAtSignout()は失敗しない。
        void procAtSignout().then(() => {
          reject({ message: error.message })
        })
      })
  })
}

/**
 * CognitoのMFA認証処理を行う。
 * MFA認証に成功した場合はセッションストレージの認証情報を更新してから、認証情報を返す。
 * @param challengeName 認証の種類
 * @param session サインイン時に発行されたセッション情報
 * @param userName ユーザーID
 * @param mfaCode MFA検証コード
 * @returns If resolved then
 * {'isExpired': (boolean)有効期限切れかどうか,
 * 'isUpdated': (boolean->true) 認証がアップデートされたかどうか,
 * 'userId': (string)ユーザーID,
 * 'tokens': {'provider': (string->'cognito')認証を提供するプロバイダ,
 * 'access_token': (string)アクセストークン,
 * 'refresh_token': (string)リフレッシュトークン,
 * 'id_token': (string)IDトークン,
 * 'session_id': (string)セッションID,
 * 'device_key': (string)デバイスキー }}.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const authChallenge = async (
  challengeName: string | null,
  session: string | null,
  userName: string | null,
  mfaCode: string | null
): Promise<AuthInfo | ErrorMessage> => {
  const args: BodyOfSigninApi = {
    function: 'auth_challenge',
    params: {
      challenge_name: challengeName,
      session: session,
      user_id: userName,
      mfa_code: mfaCode,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  if (MiiaFwConfig.config.authorization.expirationTime < Number.MAX_VALUE) {
    args.params.expiration_date_n = MiiaFwConfig.config.authorization.expirationTime
  }
  const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          if (Object.prototype.hasOwnProperty.call(data.result, 'tokens')) {
            setTokens(data.result.tokens)
            updateIsAuthorized(true)
            setUserIdToStorage(userName as string)
            setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
            updateCredentials(data.result.tokens) // 【後で削除】S3でAWS SDKを使用するために必要
            resolve(data.result)
          } else {
            reject({
              message: 'auth challenge APIが想定外の実行結果を返しました。'
            })
          }
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        // procAtSignout()は失敗しない。
        void procAtSignout().then(() => {
          reject({ message: error.message })
        })
      })
  })
}

/**
 * サインアウト処理を行う（任意の認証プロバイダで動作）。
 * @returns サインアウト処理に成功した際のメッセージをstringで返す。サインアウト処理でエラーが起きた場合はメッセージをオブジェクト{'message': '[エラーメッセージ]'}で返す。必ずresolvedになる。
 */
export const signout = async (): Promise<string | object> => {
  return new Promise(resolve => {
    // procAtSignout()は失敗しない。
    void procAtSignout().then(result => {
      resolve(result)
    })
  })
}

/**
 * Cognitoのユーザーのパスワードをリセットする。
 * @param userName ユーザーID
 * @returns If resolved then 'パスワードリセット成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const resetPassword = async (userName: string): Promise<string | ErrorMessage> => {
  const args = {
    function: 'reset_passward',
    params: {
      user_id: userName
    }
  }
  const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/**
 * Cognitoのユーザーのパスワードを初期化する（認証コードによる認証が必要）。
 * @param userName ユーザーID
 * @param verificationCode 認証コード
 * @param password パスワード初期化に用いるパスワード
 * @returns If resolved then 'パスワード初期化に成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const initializePassword = async (
  userName: string,
  verificationCode: string,
  password: string
): Promise<string | ErrorMessage> => {
  const args = {
    function: 'initialize_passward',
    params: {
      verification_code: verificationCode,
      user_id: userName,
      password: password
    }
  }
  const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/**
 * Cognitoのユーザーのパスワードを変更する。
 * @param currentPassword 現在のパスワード
 * @param newPassword 新しいパスワード
 * @returns If resolved then 'パスワード更新に成功しました。'
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const updatePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<string | ErrorMessage> => {
  const tokens = getTokens()
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      reject({ message: '未認証のユーザのパスワード更新が要求されました。' })
      void procAtSignout().then(() => {
        alert('未認証のユーザのパスワード更新が要求されました。', () => {
          if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
        })
      })
    } else {
      const upParams: UpdatePasswordParams = {
        current_password: currentPassword,
        new_password: newPassword
      }
      if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
        upParams.access_token = tokens.access_token
      }
      const args = {
        function: 'update_passward',
        params: upParams
      }
      const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
      webApiCall(
        url,
        'POST',
        MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
          ? { 'X-miiafw-token': tokens.access_token }
          : {},
        args,
        'json'
      )
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            resolve(data.result)
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
    }
  })
}

/**
 * signoutなどが呼び出す、サインアウト処理を行う本体部分。
 * セッション情報をクリアする。
 * @returns サインアウト処理に成功した際のメッセージをstringで返す。サインアウト処理でAPIの呼び出しにエラーが起きた場合はメッセージをオブジェクト{'message': '[エラーメッセージ]'}で返す。必ずresolvedになる。
 */
export const procAtSignout = async (): Promise<string | object> => {
  const tokens = getTokens()
  clearTokens()
  cleanupAuthInfoInStore()
  // S3でAWS SDKを使用するために必要
  // サインアウト実行後に必ずキャッシュクリア(ユーザを変えてサインインしたときにエラーとなる。)
  // TODO: configの型問題解消
  if (config.credentials != null && (config as any).credentials.clearCachedId != null) {
    ;(config as any).credentials.clearCachedId()
  }
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      resolve('サインアウト不要です。')
    } else {
      if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'cognito'
      ) {
        const soParams: SignoutParams = {}
        if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
          soParams.access_token = tokens.access_token
        }
        const args = { function: 'sign_out', params: soParams }
        const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
        webApiCall(
          url,
          'POST',
          MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
            ? { 'X-miiafw-token': tokens.access_token }
            : {},
          args,
          'json'
        )
          .then(data => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              resolve(data.error)
            } else {
              resolve('サインアウトが成功しました。')
            }
          })
          .catch(error => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            resolve({ message: error.message })
          })
      } else if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'twitter'
      ) {
        // 未実装
      } else {
        reject({ message: '対応していないIDプロバイダが設定されました。' })
      }
    }
  })
}

/**
 * 認証されているかどうかをチェックする。
 * 認証されていればIDプロバイダを返し、認証されていなかったりエラーが起こったりしたときにはサインアウト処理を行いエラーメッセージを返す。
 * @returns If resolved then {provider: (str)IDプロバイダ, isRefreshed: (boolean)トークンをリフレッシュしたか}.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const isAuthenticated = async (): Promise<object | ErrorMessage> => {
  const tokens = getTokens()
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      // procAtSignout()は失敗しない。
      void procAtSignout().then(() => {
        reject({ message: 'サインアウト中です。' })
      })
    } else {
      if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'cognito'
      ) {
        const tcParams: TokenCheckParams = {}
        if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
          tcParams.access_token = tokens.access_token
        }
        const args = { function: 'token_check', params: tcParams }
        const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
        webApiCall(
          url,
          'POST',
          MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
            ? { 'X-miiafw-token': tokens.access_token }
            : {},
          args,
          'json'
        )
          .then(data => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              // procAtSignout()は失敗しない。
              void procAtSignout().then(() => {
                reject({ message: data.error })
              })
            } else {
              if (data.result.isValid && data.result.userId) {
                // setTokens(tokens) // トークンはリフレッシュ後でもSession StrageやCookieに残るので不要
                // setUserIdToStorage(data.result.userId) // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
                updateCredentials(tokens) // リフレッシュ後に揮発するので要設定後で削除】S3でAWS SDKを使用するために必要
                updateIsAuthorized(true) // リフレッシュ後に揮発するので要設定
                resolve({ provider: tokens.provider, isRefreshed: false })
              } else {
                refreshToken()
                  .then(() => {
                    resolve({ provider: tokens.provider, isRefreshed: true })
                  })
                  .catch(refreshError => {
                    // procAtSignout()は失敗しない。
                    void procAtSignout().then(() => {
                      reject({ message: refreshError.message })
                    })
                  })
              }
            }
          })
          .catch(error => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            // procAtSignout()は失敗しない。
            void procAtSignout().then(() => {
              reject({ message: error.message })
            })
          })
      } else if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'twitter'
      ) {
        // console.log('twitter')
        resolve({ provider: tokens.provider, isRefreshed: false })
      } else {
        reject({ message: '対応していないIDプロバイダが設定されました。' })
      }
    }
  })
}

/**
 * トークンをリフレッシュする。
 * @returns If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const refreshToken = async (): Promise<TokenInfo | string | null | ErrorMessage> => {
  const miiaFwStore = useMiiaFwStore()
  const { refreshState } = storeToRefs(miiaFwStore)
  // console.log('リフレッシュ要求実行')
  const tokens = getTokens()
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      reject({ message: 'サインアウト中です。' })
    } else {
      // refreshState.onRefreshがtrue -> falseに変わる(リフレッシュ処理が完了する)のを待つ
      if (refreshState.value?.onRefresh) {
        const unwatch = watch(
          refreshState,
          newVal => {
            if (newVal?.onRefresh === false) {
              // リフレッシュが完了した場合
              if (newVal.error) {
                reject({ message: newVal.error })
              } else {
                // console.log('リフレッシュ処理回避')
                const refreshedTokens = getTokens()
                resolve(refreshedTokens)
              }
            }
            unwatch() // 監視を解除
          },
          { deep: true }
        )
        // タイムアウト処理はあえて実装しない。(Tokenリフレッシュされずに放置が多重で発生するリスクが極めて低いため)
      } else {
        updateRefreshState(true) // リフレッシュロックフラグを有効化(refreshTokenProc()の処理内で無効化)
        if (
          Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
          tokens.provider.toLowerCase() === 'cognito'
        ) {
          const { ttl } = MiiaFwConfig.config.authorization
          if (typeof ttl !== 'number') {
            throw new Error('[refreshToken/config] ttl を設定する必要があります')
          }
          const now = Date.now()
          // console.log('現在の日時(エポックミリ秒): ' + now)
          // console.log('前回APIコール日時: ' + getLastAccessDate())
          // console.log('前回APIコール日時からの経過時間[ms]: ' + (now - getLastAccessDate()))
          // console.log('TTL: ' + MiiaFwConfig.config.authorization.ttl)
          if (now - getLastAccessDate() < ttl) {
            // console.log('リフレッシュ処理開始')
            refreshTokenProc(tokens)
              .then(result => {
                resolve(result)
              })
              .catch(error => {
                reject({ message: error.message })
              })
          } else {
            // console.log('パスワードダイアログ表示')
            recertification(tokens)
              .then(result => {
                resolve(result)
              })
              .catch(error => {
                reject({ message: error.message })
              })
          }
        } else if (
          Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
          (tokens.provider.toLowerCase() === 'google' ||
            tokens.provider.toLowerCase() === 'facebook' ||
            tokens.provider.toLowerCase() === 'twitter')
        ) {
          recertification(tokens)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject({ message: error.message })
            })
        } else {
          updateRefreshState(false, '対応していないIDプロバイダが設定されました。')
          reject({ message: '対応していないIDプロバイダが設定されました。' })
        }
      }
    }
  })
}

/**
 * 再認証およびトークンをリフレッシュする。
 * @param tokens getTokensで取得できるセッション情報
 * @returns If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const recertification = async (
  tokens: TokenInfo
): Promise<TokenInfo | string | ErrorMessage> => {
  return new Promise((resolve, reject) => {
    if (
      Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
      tokens.provider.toLowerCase() === 'cognito'
    ) {
      promptDialog(
        'パスワードを入力してください。',
        'password',
        (password: string | null) => {
          const userId = getUserIdToStorage()
          signin(userId, password)
            .then(() => {
              // 有効期限切れ対応はしない。(サインイン時のみ)
              refreshTokenProc(tokens)
                .then(result => {
                  resolve(result)
                })
                .catch(error => {
                  reject({ message: error.message })
                })
            })
            .catch(error => {
              alert(error.message, () => {
                // 認証成功するまで再帰的に自分を実行
                recertification(tokens)
                  .then(result => {
                    resolve(result)
                  })
                  .catch(error => {
                    reject({ message: error.message })
                  })
              })
            })
        },
        () => {
          const message = 'サインアウトします。よろしいですか？'
          const okCallback = () => {
            void procAtSignout().then(() => {
              updateRefreshState(false, `サインアウトされました。(${Date.now()})`) // リフレッシュ時の待ち状態解除のため
              setLoadingView(false) // ここで処理中表示を解除しないとタイマ発火(最大5秒)まで処理中になってしまう。
              if (router.currentRoute.value.path !== '/') {
                void router.push({ path: '/' })
              } // 同じページに遷移させるとNavigation Errorになる。
            })
          }
          const cancelCallback = () => {
            // 再帰的に自分を実行
            recertification(tokens)
              .then(result => {
                resolve(result)
              })
              .catch(error => {
                reject({ message: error.message })
              })
          }
          confirm(message, okCallback, cancelCallback)
        }
        // 'large'
      )
    } else if (
      Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
      tokens.provider.toLowerCase() === 'twitter'
    ) {
      // TODO: Twitterサインイン画面によるサインイン処理
    } else {
      updateRefreshState(false, '対応していないIDプロバイダが設定されました。')
      reject({ message: '対応していないIDプロバイダが設定されました。' })
    }
  })
}

/**
 * トークンのでのリフレッシュ処理の本体(cognito認証専用)。
 * @param {Object} tokens {refresh_token: '(string)リフレッシュトークン'}
 * @returns {Promise<object>} If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
const refreshTokenProc = async (tokens: TokenInfo): Promise<TokenInfo | ErrorMessage> => {
  return new Promise((resolve, reject) => {
    const rtParams: RefreshTokenParams = {
      refresh_token: tokens.refresh_token
    }
    if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
      rtParams.access_token = tokens.access_token
    }
    const args = { function: 'refresh_token', params: rtParams }
    if (
      Object.prototype.hasOwnProperty.call(tokens, 'device_key') &&
      typeof tokens.device_key === 'string' &&
      tokens.device_key.length > 0
    ) {
      args.params.device_key = tokens.device_key
    }
    const url: string = MiiaFwConfig.config.authEndpoint.concat('/', authApiSubdir)
    webApiCall(
      url,
      'POST',
      MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
        ? { 'X-miiafw-token': tokens.access_token }
        : {},
      args,
      'json'
    )
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          updateRefreshState(false, data.error)
          reject({ message: data.error })
        } else {
          if (data.result.isUpdated) {
            setTokens(data.result.tokens)
            setUserIdToStorage(data.result.userId)
            updateIsAuthorized(true)
            updateCredentials(data.result.tokens) // 【後で削除】S3でAWS SDKを使用するために必要
          }
          updateRefreshState(false)
          resolve(data.result.tokens)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        updateRefreshState(false, error.message)
        reject({ message: error.message })
      })
  })
}

/**
 * API-GWが提供する関数を実行する(POSTメソッド限定)。
 * @param url WebAPIのURL
 * @param path WebAPIのサブディレクトリ
 * @param body リクエスト時に送信するメッセージボディ
 * @param preCb 関数実行前に呼び出されるコールバック関数
 * @param successCb 関数実行成功後に呼び出されるコールバック関数
 * @param exceptionCb 例外受信時に呼び出されるコールバック関数
 * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
 * @param allwaysCb 処理終了後に必ず呼び出されるコールバック関数
 * @param loadingView ローディング画面を表示するかどうか
 */
export const invokeApiGwFunc = (
  url: string,
  path: string,
  body: object,
  preCb: () => any,
  successCb: () => any,
  exceptionCb: () => any,
  unknownCb: () => any,
  allwaysCb: () => any,
  loadingView: boolean = true
): void => {
  if (MiiaFwConfig.config.authorization.mustAuth) {
    procUnderAuthed((tokens: TokenInfo) => {
      invokeApiGwFuncWithToken(
        tokens,
        url,
        path,
        body,
        preCb,
        successCb,
        exceptionCb,
        unknownCb,
        allwaysCb
      )
    }, loadingView)
  } else {
    invokeApiGwFuncWithoutToken(
      url,
      path,
      body,
      preCb,
      successCb,
      exceptionCb,
      unknownCb,
      allwaysCb
    )
  }
}

/**
 * invokeApiGwFuncが呼び出す、認証されているときに実行を許可する処理の雛形。
 * トークンをセッションスレージから取得出来た場合は認証の必要なAPIを実行し、取得できなかった場合にはアラートメッセージを出力してトップページに遷移させる。
 * @param callback 認証が必要な動作を行うコールバック関数
 * @param loadingView ローディング画面を表示するかどうか
 */
export const procUnderAuthed = (
  callback: (tokens: TokenInfo) => void,
  loadingView: boolean = true
): void => {
  const invokeCallback = (): void => {
    const tokens = getTokens()
    if (tokens !== null) {
      callback(tokens)
    } else {
      if (loadingView) setLoadingView(false)
      void procAtSignout().then(() => {
        alert('許可されていない操作です。(tokens)', () => {
          if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
        })
      })
    }
  }
  // 処理中表示
  if (loadingView) setLoadingView(true)
  const miiaFwStore = useMiiaFwStore()
  const { refreshState, isAuthorized } = storeToRefs(miiaFwStore)
  if (isAuthorized.value) {
    invokeCallback()
  } else {
    const unwatch = watch(isAuthorized, newVal => {
      if (newVal === true) {
        invokeCallback()
      } else {
        if (loadingView) setLoadingView(false)
        void procAtSignout().then(() => {
          alert('許可されていない操作です。(Signout)', () => {
            if (router.currentRoute.value.path !== '/') {
              void router.push({ path: '/' })
            } // 同じページに遷移させるとNavigation Errorになる。
          })
        })
      }
      unwatch() // 監視を解除
      clearTimeout(timerObj)
    })
    // 5秒以内にトークンを取得できなかった場合はタイムアウト
    const timerObj = setTimeout(() => {
      unwatch()
      if (refreshState.value?.onRefresh) {
        // トークンリフレッシュ中は再帰的に自分を実行する。
        procUnderAuthed(callback, loadingView)
      } else {
        void procAtSignout().then(() => {
          alert('許可されていない操作です。(unwatch)', () => {
            if (loadingView) setLoadingView(false)
            if (router.currentRoute.value.path !== '/') {
              void router.push({ path: '/' })
            } // 同じページに遷移させるとNavigation Errorになる。
          })
        })
      }
    }, 5000)
  }
}

// API Gatewayの認可対応のAPI実行処理本体
/**
 * invokeApiGwFuncWithTokenが呼び出す、API-GWが提供する認可処理を行った後API実行を行う処理本体。
 * @param pathParams GETメソッド使用時のパスリクエストパラメータ
 * @param method HTTPメソッド
 * @param additionalParams クエリが必要なときに入力するパラメータ
 * @param url WebAPIのURL
 * @param path WebAPIのサブディレクトリ
 * @param body リクエスト時に送信するメッセージボディ
 * @param preCb 関数実行前に呼び出されるコールバック関数
 * @param successCb 関数実行成功後に呼び出されるコールバック関数
 * @param exceptionCb 例外受信時に呼び出されるコールバック関数
 * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
 * @param allwaysCb 処理終了後に必ず呼び出されるコールバック関数
 * @param isRetryable 一度だけリトライするかどうか
 */
const invokeApiGwFuncWithAuthProc = (
  pathParams: KeyValues,
  method: string,
  additionalParams: AdditionalParams,
  url: string,
  path: string,
  body: string,
  preCb: () => any,
  successCb: (data: any) => any,
  exceptionCb: (error: any) => any,
  unknownCb: () => any,
  allwaysCb: () => any,
  isRetryable: boolean
): void => {
  const setting = {
    invokeUrl: url,
    accessKey: (config as any).credentials.accessKeyId,
    secretKey: (config as any).credentials.secretAccessKey,
    sessionToken: (config as any).credentials.sessionToken,
    region: MiiaFwConfig.config.region
  }
  const apigClient = apigClientFactory.newClient(setting)
  apigClient
    .invokeApi(pathParams, path, method, additionalParams, body)
    .then((result: any) => {
      if (Object.prototype.hasOwnProperty.call(result.data, 'error')) {
        // Lambdaが例外をスローした場合
        exceptionCb(result.data.error)
      } else {
        successCb(result.data)
      }
    })
    .catch((error: any) => {
      // API GWが自動生成するAPIはCredential Errorを検出できないので1回だけリトライする。
      if (isRetryable) {
        refreshToken()
          .then(argTokens => {
            invokeApiGwFuncWithToken(
              argTokens as TokenInfo,
              url,
              path,
              body,
              preCb,
              successCb,
              exceptionCb,
              unknownCb,
              allwaysCb,
              false
            )
          })
          .catch(refreshError => {
            // procAtSignout()は失敗しない。
            // 【要検討】Refresh tokenを使用してAccess tokenをリフレッシュできなかった場合は、Top画面に戻るか再認証してリトライか?
            void procAtSignout().then(() => {
              alert(refreshError.message, () => {
                if (router.currentRoute.value.path !== '/') {
                  void router.push({ path: '/' })
                } // 同じページに遷移させるとNavigation Errorになる。
              })
            })
          })
      } else {
        console.error(error)
        unknownCb()
      }
    })
    .then(() => {
      // then -> catch -> then = done -> fail -> always
      setLastAccessDate(Date.now())
      allwaysCb()
    })
}

/**
 * API-GWが提供する関数を実行する(POST限定、トークンによる認可処理が必要)。
 * invokeApiGwFuncが呼び出す、認可が必要な関数の処理を行う関数である。
 * @param tokens getTokensで取得できるセッション情報
 * @param url WebAPIのURL
 * @param path WebAPIのサブディレクトリ
 * @param body リクエスト時に送信するメッセージボディ
 * @param preCb 関数実行前に呼び出されるコールバック関数
 * @param successCb 関数実行成功後に呼び出されるコールバック関数
 * @param exceptionCb 例外受信時に呼び出されるコールバック関数
 * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
 * @param allwaysCb 処理終了後に必ず呼び出されるコールバック関数
 * @param isRetryable 一度だけリトライするかどうか
 */
const invokeApiGwFuncWithToken = (
  tokens: TokenInfo,
  url: string,
  path: string,
  body: any,
  preCb: () => any,
  successCb: (data: any) => any,
  exceptionCb: (error: any) => any,
  unknownCb: () => any,
  allwaysCb: () => any,
  isRetryable: boolean = true
): void => {
  preCb()

  const pathParams = {
    // GET利用時の変数(クエリストリング)
  }
  const method = 'POST'
  let additionalParams = {}
  if (tokens.provider.toLowerCase() === 'cognito') {
    if (
      Object.prototype.hasOwnProperty.call(MiiaFwConfig.config.authorization, 'method') &&
      MiiaFwConfig.config.authorization.method?.toLowerCase() === 'iam'
    ) {
      // AWS_IAM
      additionalParams = {
        // AWS_IAMで認可する場合は、ヘッダを設定すると署名の検証で失敗する。
        queryParams: {}
      }
      // AWS_IAMで認可する場合は、HTTPヘッダを設定すると署名の検証で失敗するのでBodyでAccess Tokenを渡す。
      if (Object.prototype.hasOwnProperty.call(body, 'params')) {
        body.access_token = tokens.access_token
        body.session_id = tokens.session_id
      }
      ;(config as any).credentials.get((error: string) => {
        if (!error) {
          invokeApiGwFuncWithAuthProc(
            pathParams,
            method,
            additionalParams,
            url,
            path,
            body,
            preCb,
            successCb,
            exceptionCb,
            unknownCb,
            allwaysCb,
            isRetryable
          )
        } else {
          void refreshToken()
            .then((argTokens: any) => {
              ;(config as any).credentials.get((err: string) => {
                if (!err) {
                  body.access_token = argTokens.access_token
                  body.session_id = argTokens.session_id
                  invokeApiGwFuncWithAuthProc(
                    pathParams,
                    method,
                    additionalParams,
                    url,
                    path,
                    body,
                    preCb,
                    successCb,
                    exceptionCb,
                    unknownCb,
                    allwaysCb,
                    isRetryable
                  )
                } else {
                  void procAtSignout().then(() => {
                    alert(err, () => {
                      if (router.currentRoute.value.path !== '/') {
                        void router.push({ path: '/' })
                      } // 同じページに遷移させるとNavigation Errorになる。
                    })
                  })
                }
              })
            })
            .catch(refreshError => {
              // procAtSignout()は失敗しない。
              // 【要検討】Refresh tokenを使用してAccess tokenをリフレッシュできなかった場合は、Top画面に戻るか再認証してリトライか?
              void procAtSignout().then(() => {
                alert(refreshError.message, () => {
                  if (router.currentRoute.value.path !== '/') {
                    void router.push({ path: '/' })
                  } // 同じページに遷移させるとNavigation Errorになる。
                })
              })
            })
        }
      })
    } else if (
      Object.prototype.hasOwnProperty.call(MiiaFwConfig.config.authorization, 'method') &&
      MiiaFwConfig.config.authorization.method?.toLowerCase() === 'custom_authorizer'
    ) {
      additionalParams = {
        // Custom Authorizerで認可する場合は、Authorizationヘッダにid tokenを含める必要あり
        headers: {
          Authorization: tokens.id_token,
          'X-miiafw-token': tokens.access_token
        },
        queryParams: {}
      }
      invokeApiGwFuncWithAuthProc(
        pathParams,
        method,
        additionalParams,
        url,
        path,
        body,
        preCb,
        successCb,
        exceptionCb,
        unknownCb,
        allwaysCb,
        isRetryable
      )
    } else {
      void procAtSignout().then(() => {
        alert('対応していない認可方式です。', () => {
          if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
        })
      })
    }
  } else if (tokens.provider.toLowerCase() === 'google') {
    additionalParams = {
      headers: { Authorization: tokens.id_token },
      queryParams: {}
    }
    invokeApiGwFuncWithAuthProc(
      pathParams,
      method,
      additionalParams,
      url,
      path,
      body,
      preCb,
      successCb,
      exceptionCb,
      unknownCb,
      allwaysCb,
      isRetryable
    )
  } else if (tokens.provider.toLowerCase() === 'facebook') {
    additionalParams = {
      // facebook: accessトークン
      headers: { Authorization: tokens.access_token },
      queryParams: {}
    }
    invokeApiGwFuncWithAuthProc(
      pathParams,
      method,
      additionalParams,
      url,
      path,
      body,
      preCb,
      successCb,
      exceptionCb,
      unknownCb,
      allwaysCb,
      isRetryable
    )
  } else if (tokens.provider.toLowerCase() === 'twitter') {
    additionalParams = {
      // twitter: Authトークンとトークンシークレットを「;」で連結したもの

      headers: { Authorization: `${tokens.auth_token};${tokens.token_secret}` },
      queryParams: {}
    }
    invokeApiGwFuncWithAuthProc(
      pathParams,
      method,
      additionalParams,
      url,
      path,
      body,
      preCb,
      successCb,
      exceptionCb,
      unknownCb,
      allwaysCb,
      isRetryable
    )
  } else {
    void procAtSignout().then(() => {
      alert('対応していないIDプロバイダが設定されました。', () => {
        if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
      })
    })
  }
}

/**
 * API-GWが提供する関数を実行する(POST限定、トークンによる認可処理が不要)。
 * invokeApiGwFuncが呼び出す、認可が不要な関数の処理を行う関数である。
 * @param url WebAPIのURL
 * @param path WebAPIのサブディレクトリ
 * @param body リクエスト時に送信するメッセージボディ
 * @param preCb 関数実行前に呼び出されるコールバック関数
 * @param successCb 関数実行成功後に呼び出されるコールバック関数
 * @param exceptionCb 例外受信時に呼び出されるコールバック関数
 * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
 * @param allwaysCb 処理終了後に必ず呼び出されるコールバック関数
 */
const invokeApiGwFuncWithoutToken = (
  url: string,
  path: string,
  body: object,
  preCb: () => any,
  successCb: (data: any) => any,
  exceptionCb: (error: any) => any,
  unknownCb: () => any,
  allwaysCb: () => any
): void => {
  const invokeApi = async (uri: string, params: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      webApiCall(uri, 'POST', {}, params, 'json')
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          resolve(data)
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
    })
  }
  preCb()
  void invokeApi(url + path, body)
    .then((data: any) => {
      if (Object.prototype.hasOwnProperty.call(data, 'error')) {
        // Lambdaが例外をスローした場合
        exceptionCb(data.error)
      } else {
        successCb(data)
      }
    })
    .catch(error => {
      console.error(error)
      unknownCb()
    })
    .then(() => {
      // then -> catch -> then = done -> fail -> always
      setLastAccessDate(Date.now())
      allwaysCb()
    })
}

// トークンからユーザ属性を取得する。
export const getUserAttributesFromStore = async (): Promise<any> => {
  const miiaFwStore = useMiiaFwStore()
  const { attributes } = storeToRefs(miiaFwStore)
  return new Promise((resolve, reject) => {
    if (attributes.value !== undefined) {
      resolve(attributes.value)
    } else {
      const unwatch = watch(attributes, (_newVal, oldVal) => {
        if (oldVal === undefined) {
          resolve(attributes.value)
        } else {
          // 処理なし
        }
        unwatch() // 監視を解除
        clearTimeout(timerObj)
      })
      // 5秒以内にトークンからユーザ属性を取得できなかった場合はタイムアウト
      const timerObj = setTimeout(() => {
        unwatch()
        reject({ message: 'ユーザ属性の取得に失敗しました。' })
      }, 5000)
    }
  })
}

/**
 * セッションストレージへセッション情報を格納する。
 * @param tokens セッション情報
 */
const setTokens = (tokens: Partial<TokenInfo> & Pick<TokenInfo, 'provider'>): void => {
  // Cookieから取得 → そのまま、Bodyから取得 → セッションストレージに格納する。
  if (getSessionInfo('access_token').length < 100 || getSessionInfo('id_token').length < 100) {
    tempDataSetItem('access_token', tokens.access_token ? tokens.access_token : '')
    tempDataSetItem('refresh_token', tokens.refresh_token ? tokens.refresh_token : '')
    tempDataSetItem('id_token', tokens.id_token ? tokens.id_token : '')
    tempDataSetItem('device_key', tokens.device_key ? tokens.device_key : '')
    tempDataSetItem('auth_token', tokens.auth_token ? tokens.auth_token : '')
    tempDataSetItem('token_secret', tokens.token_secret ? tokens.token_secret : '')
    tempDataSetItem('session_id', tokens.session_id ? tokens.session_id : '')
    tempDataSetItem('provider', tokens.provider)
  } else {
    // Cookieのサイズ制限があるのでリフレッシュトークンはBodyで取得してセッション
    tempDataSetItem('refresh_token', tokens.refresh_token ? tokens.refresh_token : '')
    tempDataSetItem('provider', tokens.provider)
  }
}

/**
 * セッションストレージからセッション情報を取得する。
 * @returns {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': (string)Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}
 */
export const getTokens = (): TokenInfo | null => {
  const ret = {} as any
  let accessToken = getSessionInfo('access_token')
  if (accessToken.length === 0) {
    accessToken = tempDataGetItem('access_token')
  }
  if (accessToken) ret.access_token = accessToken
  const refreshToken = tempDataGetItem('refresh_token')
  if (refreshToken) ret.refresh_token = refreshToken
  let idToken = getSessionInfo('id_token')
  if (idToken.length === 0) {
    idToken = tempDataGetItem('id_token')
  }
  if (idToken) ret.id_token = idToken
  let deviceKey = getSessionInfo('device_key')
  if (deviceKey.length === 0) {
    deviceKey = tempDataGetItem('device_key')
  }
  if (deviceKey) ret.device_key = deviceKey
  const authToken = tempDataGetItem('auth_token')
  if (authToken) ret.auth_token = authToken
  const tokenSecret = tempDataGetItem('token_secret')
  if (tokenSecret) ret.token_secret = tokenSecret
  const sessionId = tempDataGetItem('session_id')
  if (sessionId) ret.session_id = sessionId
  const provider = tempDataGetItem('provider')
  if (provider) ret.provider = provider
  if (Object.keys(ret).length === 0) {
    return null
  } else {
    return ret
  }
}

/**
 * セッションストレージのセッション情報を消去する。
 */
export const clearTokens = (): void => {
  tempDataRemoveItem('access_token')
  tempDataRemoveItem('refresh_token')
  tempDataRemoveItem('id_token')
  tempDataRemoveItem('device_key')
  tempDataRemoveItem('auth_token')
  tempDataRemoveItem('token_secret')
  tempDataRemoveItem('provider')
  tempDataRemoveItem('session_id')
  tempDataRemoveItem('user_id') // ユーザIDも同じタイミングで消去
  tempDataRemoveItem('last_access') // 最後にWeb APIへアクセスした時間も同じタイミングで消去
  RemoveSessionInfo(['access_token', 'id_token', 'device_key'])
}

/**
 * セッションストレージへユーザIDを登録する（キー: user_id）。
 * @param userId ユーザーID
 */
const setUserIdToStorage = (userId: string): void => {
  tempDataSetItem('user_id', userId)
}

/**
 * セッションストレージからユーザIDを取得する
 * @returns ユーザーID
 */
export const getUserIdToStorage = (): string | null => {
  return tempDataGetItem('user_id')
}

/**
 * セッションストレージへ最後にWeb APIへアクセスした時間を格納する(キー: last_access)。
 * @param lad Date.now()で取得される時間
 */
export const setLastAccessDate = (lad: number): void => {
  tempDataSetItem('last_access', String(lad))
}

/**
 * セッションストレージから最後にWeb APIへアクセスした時間取得する(キー: last_access)。
 * @returns Date.now()で記録された時間を取得する。nullの場合は0を返す。
 */
const getLastAccessDate = (): number => {
  const strLad = tempDataGetItem('last_access')
  if (strLad === null) {
    return 0
  } else {
    const lad = Number(strLad)
    if (Number.isNaN(lad)) {
      return 0
    } else {
      return lad
    }
  }
}

// クレデンシャルを更新する。(S3の更新に必要)
/**
 * AWSのクレデンシャルを更新する。
 * @param tokens getTokensで取得できるセッション情報
 * @throws エラーメッセージを持つErrorオブジェクトを返す。
 */
const updateCredentials = (tokens: Partial<TokenInfo> & Pick<TokenInfo, 'provider'>): void => {
  let loginTo = ''
  const logins: { [any: string]: string } = {}
  const token = tokens.id_token
  if (tokens.provider.toLowerCase() === 'cognito' && token !== undefined) {
    loginTo = `cognito-idp.${MiiaFwConfig.config.region}.amazonaws.com/${MiiaFwConfig.config.authorization.userPoolId}`
    logins[loginTo] = token // cognito: idトークン
  } else if (tokens.provider.toLowerCase() === 'google' && token !== undefined) {
    loginTo = 'accounts.google.com'
    logins[loginTo] = token // google: idトークン
  } else if (tokens.provider.toLowerCase() === 'facebook' && token !== undefined) {
    loginTo = 'graph.facebook.com'
    logins[loginTo] = token // facebook: accessトークン
  } else if (tokens.provider.toLowerCase() === 'twitter' && token !== undefined) {
    loginTo = 'api.twitter.com'

    logins[loginTo] = `${tokens.auth_token};${tokens.token_secret}` // twitter: Authトークンとトークンシークレットを「;」で連結したもの
  } else {
    throw new Error('対応していないIDプロバイダが設定されました。')
  }
  // config.credentialsを書き換えただけでは反映はされない(この時点では、config.credentials.needsRefresh == true)
  // config.credentials.refreshを実行すると、config.credentials.needsRefresh === falseとなる。
  // config.credentials.getを実行すると、未反映の場合は暗黙的にAWS.config.credentials.refreshが実行される。
  config.update({
    region: MiiaFwConfig.config.region,
    credentials: new CognitoIdentityCredentials(
      {
        IdentityPoolId: MiiaFwConfig.config.authorization.identityPoolId!,
        Logins: logins
      },
      {
        region: MiiaFwConfig.config.region
      }
      // 古い記述
      // {
      //   region: MiiaFwConfig.config.region,
      //   IdentityPoolId: MiiaFwConfig.config.authorization.identityPoolId,
      //   Logins: logins // ES2015(ES5)の記述要領(IE対応)
      //   // Logins: { [loginTo]: value }  // ES2015(ES6)の記述要領
      // }
    )
  })
}

/**
 * Twitterが提供するAPIを利用した認証処理を行う（作成途中）。
 * Twitterのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はTwitterの認証ダイアログを開く。
 * @param _successCb 関数実行成功後に呼び出されるコールバック関数
 * @param _exceptionCb 例外受信時に呼び出されるコールバック関数
 * @param _closeCb 認証ダイアログを閉じた際に呼び出されるコールバック関数
 */

export const twitterCheckStatus = () =>
  // _successCb = () => {},
  // _exceptionCbt = (msg: string) => {
  //   console.error(msg)
  // },
  // _closeCb = () => {}
  {
    alert('作成中です！')
  }
